package com.tregz.tremenz.ui.component

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tregz.tremenz.io.model.ModelData
import com.tregz.tremenz.io.notify.NotifyPlayback
import com.tregz.tremenz.io.player.PlayerMedia
import com.tregz.tremenz.ui.icon.IconKey
import com.tregz.tremenz.ui.icon.iconPainter
import com.tregz.tremenz.ui.label.LabelKey
import com.tregz.tremenz.ui.label.labelText
import com.tregz.tremenz.ui.media.MediaEmbedAudio
import com.tregz.tremenz.ui.media.MediaEmbedVideo
import com.tregz.tremenz.ui.media.MediaImage
import com.tregz.tremenz.ui.media.MediaOverlay
import com.tregz.tremenz.ui.media.MediaSurface

@Composable
fun ContainerPlayer(
    mediaItem: ModelData.Media?,
    player: PlayerMedia,
    isPreparingVideo: Boolean,
    isVideoPlaying: Boolean,
    onVideoPlayToggle: (Boolean) -> Unit,
    isPreparingSong: Boolean = false,
    isSongPlaying: Boolean = false,
    onSongStateChange: (Int) -> Unit = {},
    onSongError: (Int) -> Unit = {},
    onSongEnding: () -> Unit = {},
    onSongAdDetected: () -> Unit = {},
    onStop: () -> Unit = {},
    fsNotification: NotifyPlayback?,
    modifier: Modifier = Modifier
) {
    // The page background, so the carousel's surround follows the day and night theme like the
    // tab listing below it; the media surfaces keep their own black behind a playing video.
    Box(
        modifier = modifier
            .background(MaterialTheme.colorScheme.background)
    ) {
        if (mediaItem is ModelData.Song) {
            MediaEmbedVideo(
                videoId = mediaItem.id,
                isPlaying = isSongPlaying,
                onStateChange = onSongStateChange,
                onError = onSongError,
                onEnding = onSongEnding,
                onAdDetected = onSongAdDetected,
                modifier = Modifier.fillMaxSize()
            )

            // Poster and loading overlay while preparing YouTube playback
            if (isPreparingSong) {
                MediaImage(
                    url = mediaItem.posterUrl,
                    contentDescription = null,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop
                )

                // Translucent scrim to enhance contrast for the spotlight animation
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .background(Color.Black.copy(alpha = 0.35f))
                )

                MediaOverlay(modifier = Modifier.align(Alignment.Center))
                PrepareCancel(onStop, Modifier.align(Alignment.BottomEnd))
            }
        } else if (mediaItem is ModelData.Video) {
            var hasPreparedThisVideo by remember(mediaItem.id) { mutableStateOf(false) }
            // Latched in an effect: writing state while composing invalidates the composition it is in.
            LaunchedEffect(mediaItem.id, isVideoPlaying) {
                if (isVideoPlaying) hasPreparedThisVideo = true
            }

            MediaSurface(
                player = player,
                modifier = Modifier.fillMaxSize()
            )

            // Video placeholder artwork poster
            if (isPreparingVideo || !hasPreparedThisVideo) {
                MediaImage(
                    url = mediaItem.posterUrl,
                    contentDescription = null,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop
                )
            }

            if (isPreparingVideo) {
                // Translucent scrim to enhance contrast for the spotlight animation
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .background(Color.Black.copy(alpha = 0.35f))
                )

                MediaOverlay(modifier = Modifier.align(Alignment.Center))
                PrepareCancel(onStop, Modifier.align(Alignment.BottomEnd))
            } else {
                // Custom video controls overlay
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(16.dp)
                ) {
                    Text(
                        text = mediaItem.toString(),
                        color = Color.White,
                        fontSize = 18.sp,
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier
                            .align(Alignment.TopStart)
                            .background(Color.Black.copy(alpha = 0.6f))
                            .padding(4.dp)
                    )

                    IconButton(
                        onClick = {
                            // The same state the icon shows, so the label and the action never disagree.
                            if (isVideoPlaying) {
                                player.pause()
                                onVideoPlayToggle(false)
                                fsNotification?.onPlayChanged(false)
                            } else {
                                player.start()
                                onVideoPlayToggle(true)
                                fsNotification?.onPlayChanged(true)
                            }
                        },
                        modifier = Modifier
                            .size(56.dp)
                            .align(Alignment.Center)
                            .background(Color.Black.copy(alpha = 0.5f), RoundedCornerShape(28.dp))
                    ) {
                        Icon(
                            painter = iconPainter(
                                if (isVideoPlaying) IconKey.PAUSE else IconKey.PLAY
                            ),
                            contentDescription = labelText(if (isVideoPlaying) LabelKey.PLAYER_PAUSE else LabelKey.PLAYER_PLAY),
                            tint = Color.White,
                            modifier = Modifier.size(36.dp)
                        )
                    }
                }
            }
        } else if (mediaItem is ModelData.Audio) {
            MediaEmbedAudio(
                url = mediaItem.sono,
                modifier = Modifier.fillMaxSize()
            )
        } else {
            CarouselHero(modifier = Modifier.fillMaxSize())
        }
    }
}

/** A prepare can stall with no timeout, and the play control is hidden until it ends: this stops it. */
@Composable
private fun PrepareCancel(onStop: () -> Unit, modifier: Modifier) {
    TextButton(onClick = onStop, modifier = modifier.padding(8.dp)) {
        Text(text = labelText(LabelKey.ACTION_CANCEL), color = Color.White)
    }
}
